import { nextTick, onBeforeUnmount, onMounted } from 'vue';

// `[tabindex="-1"]` fica de fora de propósito, e em TODA cláusula — não só
// na genérica do final. Antes da expansão lateral, nada aqui punha
// tabindex="-1" numa tag nativamente focável: o único uso programático
// (a mensagem citada pelo resumo) é num elemento sem interatividade nativa,
// então só a cláusula `[tabindex]` bare precisava excluir. O painel de
// detalhe introduziu o padrão WAI-ARIA de abas com tabindex circulante — a
// aba INATIVA é um `<button role="tab" tabindex="-1">`, nativamente focável
// pela cláusula `button:not([disabled])` sozinha, que não olha tabindex
// nenhum. Sem a exclusão em cada cláusula nativa, a armadilha tratava as
// DUAS abas como parada de Tab, quando só a ativa deveria ser uma — a
// inativa só é alcançável pelas setas, como o padrão exige. O próprio
// contêiner do diálogo segue de fora pela mesma regra (ele só é focável
// via a cláusula bare, e tabindex="-1" nela já o exclui).
export const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  // `summary` é focável NATIVAMENTE e não casa com nenhuma cláusula acima (não
  // tem `tabindex` no HTML). Sem esta linha, o foco pousa nele, `isAtBorder`
  // (que é `!elements.includes(active)`) vira true, e o Tab para frente volta
  // ao PRIMEIRO elemento — pulando tudo que vem depois. Medido em 2026-08-20
  // com o aviso do resumo em `details/summary`: 9 de 20 controles do diálogo
  // ficaram inalcançáveis avançando, incluindo o painel de detalhe inteiro.
  // Reprova 2.4.3 (Focus Order, nível A).
  'summary:not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Prende o foco dentro de um diálogo modal e o devolve, ao fechar, ao
 * elemento que estava focado quando ele abriu.
 *
 * São três garantias distintas, e nenhuma delas é gratuita num modal feito
 * com <div role="dialog"> (o <dialog> nativo daria as duas primeiras, mas
 * `showModal()` não existe em jsdom, e um foco que só o navegador real
 * exercita é um foco que teste nenhum protege):
 *   - 2.4.3 Focus Order: Tab no último elemento volta ao primeiro em vez de
 *     escapar para o board ATRÁS do modal, que está visualmente coberto;
 *   - 2.1.2 lido ao contrário: Escape sempre devolve o controle, então o
 *     laço acima nunca vira armadilha;
 *   - 2.4.3 de novo, na saída: fechar devolve o foco ao gatilho, o mesmo
 *     contrato que usePopoverTrigger já cumpre para os popovers do board.
 *
 * O ouvinte é de `document` em fase de captura, não do contêiner: com o
 * foco no elemento raiz do diálogo (caso sem nenhum controle dentro) um
 * ouvinte do próprio contêiner ainda pegaria o evento, mas qualquer conteúdo
 * que teleporte para fora dele (menu flutuante de uma mensagem) sairia do
 * alcance — e é justamente aí que Escape precisa continuar funcionando.
 *
 * @param {import('vue').Ref<HTMLElement|null>} containerRef contêiner do diálogo.
 * @param {Object} [handlers]
 * @param {Function} [handlers.onEscape] chamado quando Escape é pressionado.
 */
export function useFocusTrap(containerRef, { onEscape } = {}) {
  let previouslyFocused = null;

  const focusableElements = () =>
    Array.from(containerRef.value?.querySelectorAll(FOCUSABLE_SELECTOR) ?? []);

  const focusFirstElement = () => {
    const [first] = focusableElements();
    (first ?? containerRef.value)?.focus();
  };

  const wrapTabFocus = event => {
    const container = containerRef.value;
    if (!container) return;

    const elements = focusableElements();
    if (elements.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }

    const first = elements[0];
    const last = elements[elements.length - 1];
    const active = document.activeElement;
    // Foco fora do contêiner conta como borda: é o estado em que o modal
    // acabou de abrir e nada dentro dele foi focado ainda, e deixá-lo
    // passar devolveria o Tab para o board coberto.
    const isInside = container.contains(active);
    // Foco DENTRO do contêiner num elemento que não participa do ciclo de Tab
    // (tabindex="-1" recebido programaticamente — é o caso da mensagem citada
    // pelo resumo, para onde a âncora leva o foco) também é borda: a partir
    // dele o navegador seguiria para o próximo tabulável do DOCUMENTO, que
    // depois da última região do diálogo já está fora dele.
    const isAtBorder = !isInside || !elements.includes(active);

    if (event.shiftKey && (isAtBorder || active === first)) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && (isAtBorder || active === last)) {
      event.preventDefault();
      first.focus();
    }
  };

  const onKeydown = event => {
    if (event.key === 'Escape') {
      // stopPropagation para que Escape feche SÓ o modal: os popovers do
      // board escutam o mesmo teclado e fechariam junto, invisíveis atrás
      // do diálogo.
      event.stopPropagation();
      onEscape?.();
      return;
    }
    if (event.key === 'Tab') wrapTabFocus(event);
  };

  onMounted(() => {
    previouslyFocused = document.activeElement;
    // O contêiner só existe depois do primeiro flush do teleporte.
    nextTick(focusFirstElement);
    document.addEventListener('keydown', onKeydown, true);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown, true);
    previouslyFocused?.focus?.();
  });
}
